import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Mic, MicOff, Send, Volume2, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Ciao! Sono Riccardo. Come posso aiutarti con la tua scheda Google o il tuo business?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [playingAudio, setPlayingAudio] = useState(false);
  const messagesEnd = useRef(null);
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendText = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Mi dispiace, c'e' stato un problema. Riprova o scrivimi su WhatsApp!" }]);
    }
    setLoading(false);
  }, [input, loading, sessionId]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      audioChunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunks.current, { type: "audio/webm" });
        await sendVoice(blob);
      };
      mr.start();
      mediaRecorder.current = mr;
      setRecording(true);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Non riesco ad accedere al microfono. Devi autorizzare il browser. Ecco come fare:\n\n" +
        "Chrome: clicca il lucchetto nella barra degli indirizzi > Autorizzazioni > Microfono > Consenti\n\n" +
        "Safari: Impostazioni > Siti web > Microfono > Consenti\n\n" +
        "Firefox: clicca il lucchetto > Autorizzazioni > Microfono > Consenti\n\n" +
        "Oppure scrivimi a testo, funziona uguale!" }]);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      mediaRecorder.current.stop();
    }
    setRecording(false);
  };

  const sendVoice = async (blob) => {
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: "🎙️ Messaggio vocale..." }]);
    const fd = new FormData();
    fd.append("audio", blob, "voice.webm");
    fd.append("session_id", sessionId);
    try {
      const res = await fetch(`${API}/api/chat/voice`, { method: "POST", body: fd });
      const sid = res.headers.get("X-Session-Id");
      const userText = res.headers.get("X-User-Text");
      const replyText = res.headers.get("X-Reply-Text");
      if (sid) setSessionId(sid);
      // Update user message with transcription
      if (userText) {
        setMessages((m) => {
          const copy = [...m];
          const lastUser = copy.findLastIndex((msg) => msg.role === "user");
          if (lastUser >= 0) copy[lastUser] = { role: "user", text: userText };
          return copy;
        });
      }
      // Add bot reply text
      if (replyText) setMessages((m) => [...m, { role: "bot", text: replyText }]);
      // Play audio
      const audioBlob = await res.blob();
      const url = URL.createObjectURL(audioBlob);
      const audio = new Audio(url);
      setPlayingAudio(true);
      audio.onended = () => { setPlayingAudio(false); URL.revokeObjectURL(url); };
      audio.play().catch(() => setPlayingAudio(false));
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Errore nella comunicazione vocale. Riprova!" }]);
    }
    setLoading(false);
  };

  const speakReply = async (text) => {
    if (playingAudio) return;
    setPlayingAudio(true);
    try {
      const res = await fetch(`${API}/api/chat/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { setPlayingAudio(false); URL.revokeObjectURL(url); };
      audio.play().catch(() => setPlayingAudio(false));
    } catch {
      setPlayingAudio(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-[#D4AF37] hover:bg-[#B8942E] text-black shadow-xl flex items-center justify-center transition-all hover:scale-110"
        data-testid="chatbot-fab"
        title="Chatta con Riccardo"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 left-6 z-50 w-[370px] max-w-[calc(100vw-3rem)] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-[#D4AF37]/30 bg-[#0c1220]" data-testid="chatbot-window" style={{ height: "520px" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#0c1220] border-b border-[#D4AF37]/20">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#D4AF37] flex items-center justify-center text-black font-bold text-sm">RR</div>
          <div>
            <p className="text-white font-bold text-sm leading-tight">Riccardo Rossi</p>
            <p className="text-[#D4AF37] text-[10px]">Consulente Google Business Profile</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white transition-colors" data-testid="chatbot-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollbarWidth: "thin" }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-[#D4AF37] text-black rounded-br-md"
                  : "bg-white/10 text-gray-200 rounded-bl-md"
              }`}
            >
              {msg.text}
              {msg.role === "bot" && i > 0 && (
                <button
                  onClick={() => speakReply(msg.text)}
                  className="ml-2 inline-flex items-center text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors"
                  title="Ascolta"
                  disabled={playingAudio}
                >
                  <Volume2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white/10 rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
            </div>
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      {/* Input */}
      <div className="px-3 py-3 border-t border-white/10 bg-[#0c1220]">
        <div className="flex items-center gap-2">
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={loading}
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              recording
                ? "bg-red-500 hover:bg-red-600 animate-pulse"
                : "bg-white/10 hover:bg-white/20 text-[#D4AF37]"
            }`}
            data-testid="chatbot-mic"
            title={recording ? "Ferma registrazione" : "Parla"}
          >
            {recording ? <MicOff className="w-4.5 h-4.5 text-white" /> : <Mic className="w-4.5 h-4.5" />}
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendText()}
            placeholder="Scrivi un messaggio..."
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/40"
            data-testid="chatbot-input"
            disabled={loading || recording}
          />
          <button
            onClick={sendText}
            disabled={!input.trim() || loading}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-[#D4AF37] hover:bg-[#B8942E] text-black flex items-center justify-center transition-colors disabled:opacity-30"
            data-testid="chatbot-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        {recording && (
          <p className="text-red-400 text-[10px] text-center mt-1.5 animate-pulse">Sto ascoltando... clicca per inviare</p>
        )}
        {playingAudio && (
          <p className="text-[#D4AF37] text-[10px] text-center mt-1.5">Riccardo sta parlando...</p>
        )}
        {!recording && !playingAudio && (
          <p className="text-gray-500 text-[10px] text-center mt-1.5">
            Microfono non funziona?{" "}
            <button onClick={() => setMessages(m => [...m, { role: "bot", text: "Per attivare il microfono:\n\nChrome: clicca il lucchetto (o l'icona a sinistra dell'URL) > Autorizzazioni sito > Microfono > Consenti. Poi ricarica la pagina.\n\nSafari (iPhone/Mac): Impostazioni > Safari > Microfono > Consenti. Oppure tieni premuto il lucchetto nella barra.\n\nFirefox: clicca il lucchetto > Connessione sicura > Altre informazioni > Autorizzazioni > Microfono > Consenti.\n\nSe sei su iPhone e non funziona: apri il sito in Safari (non Chrome). iOS permette il microfono solo su Safari.\n\nOppure scrivi pure a testo qui sotto, ti rispondo uguale!" }])} className="text-[#D4AF37] hover:underline cursor-pointer">Guida rapida</button>
          </p>
        )}
      </div>
    </div>
  );
}
